export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3,
}

export interface LoggerOptions {
  level: LogLevel;
  prefix?: string;
  timestamp?: boolean;
  colors?: boolean;
}

export interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: Date;
  prefix?: string;
  data?: any;
  error?: Error;
}

export class Logger {
  private options: LoggerOptions;
  private handlers: ((entry: LogEntry) => void)[] = [];

  constructor(options: Partial<LoggerOptions> = {}) {
    this.options = {
      level: LogLevel.INFO,
      timestamp: true,
      colors: true,
      ...options,
    };
  }

  /**
   * Add a custom log handler
   */
  addHandler(handler: (entry: LogEntry) => void): void {
    this.handlers.push(handler);
  }

  /**
   * Remove a log handler
   */
  removeHandler(handler: (entry: LogEntry) => void): void {
    const index = this.handlers.indexOf(handler);
    if (index !== -1) {
      this.handlers.splice(index, 1);
    }
  }

  /**
   * Log an error message
   */
  error(message: string, error?: Error | any): void {
    this.log(LogLevel.ERROR, message, error);
  }

  /**
   * Log a warning message
   */
  warn(message: string, data?: any): void {
    this.log(LogLevel.WARN, message, data);
  }

  /**
   * Log an info message
   */
  info(message: string, data?: any): void {
    this.log(LogLevel.INFO, message, data);
  }

  /**
   * Log a debug message
   */
  debug(message: string, data?: any): void {
    this.log(LogLevel.DEBUG, message, data);
  }

  /**
   * Core logging method
   */
  private log(level: LogLevel, message: string, data?: any): void {
    if (level > this.options.level) {
      return;
    }

    const entry: LogEntry = {
      level,
      message,
      timestamp: new Date(),
      prefix: this.options.prefix,
      data,
    };

    if (data instanceof Error) {
      entry.error = data;
      entry.data = {
        name: data.name,
        message: data.message,
        stack: data.stack,
      };
    }

    // Call custom handlers
    this.handlers.forEach(handler => handler(entry));

    // Default console output
    this.consoleOutput(entry);
  }

  /**
   * Format and output to console
   */
  private consoleOutput(entry: LogEntry): void {
    const parts: string[] = [];

    // Timestamp
    if (this.options.timestamp) {
      parts.push(`[${entry.timestamp.toISOString()}]`);
    }

    // Level
    const levelName = LogLevel[entry.level];
    if (this.options.colors) {
      parts.push(this.colorize(levelName, entry.level));
    } else {
      parts.push(`[${levelName}]`);
    }

    // Prefix
    if (entry.prefix) {
      parts.push(`[${entry.prefix}]`);
    }

    // Message
    parts.push(entry.message);

    const logMessage = parts.join(' ');

    // Output based on level
    switch (entry.level) {
      case LogLevel.ERROR:
        console.error(logMessage);
        if (entry.error) {
          console.error(entry.error);
        }
        break;
      case LogLevel.WARN:
        console.warn(logMessage);
        break;
      case LogLevel.DEBUG:
        console.debug(logMessage);
        break;
      default:
        console.log(logMessage);
    }

    // Log additional data if present
    if (entry.data && !entry.error) {
      console.log(JSON.stringify(entry.data, null, 2));
    }
  }

  /**
   * Colorize text based on log level
   */
  private colorize(text: string, level: LogLevel): string {
    const colors = {
      [LogLevel.ERROR]: '\x1b[31m', // Red
      [LogLevel.WARN]: '\x1b[33m',  // Yellow
      [LogLevel.INFO]: '\x1b[36m',  // Cyan
      [LogLevel.DEBUG]: '\x1b[90m', // Gray
    };

    const color = colors[level] || '';
    const reset = '\x1b[0m';

    return `${color}[${text}]${reset}`;
  }

  /**
   * Create a child logger with a prefix
   */
  child(prefix: string): Logger {
    const childPrefix = this.options.prefix 
      ? `${this.options.prefix}:${prefix}`
      : prefix;

    const child = new Logger({
      ...this.options,
      prefix: childPrefix,
    });

    // Copy handlers
    this.handlers.forEach(handler => child.addHandler(handler));

    return child;
  }

  /**
   * Set the log level
   */
  setLevel(level: LogLevel): void {
    this.options.level = level;
  }

  /**
   * Get the current log level
   */
  getLevel(): LogLevel {
    return this.options.level;
  }
}

// Default logger instance
export const logger = new Logger();